import EntityModel from '@/common/models/entity-model';
import PlanFormulaModel from '@/common/models/plan-formula-model';
import InvoiceTimeShiftModel from '@/common/models/invoice-time-shift-model';
import PeriodAnchorInstructionModel from '@/common/models/period-anchor-instruction-model';

export default class PlanModel extends EntityModel {
    hydrate({
        id = null,
        name = null,
        isActive = true,
        productId = null,
        productOptions = {},
        currency = null,
        pricing = {},
        recurringInterval = {},
        trial = null,
        setup = null,
        subscriptionCount = null,
        createdTime = null,
        updatedTime = null,
        customFields = null,
        invoiceTimeShift = null,
    } = {}) {
        this.id = id;
        this.name = name;
        this.isActive = isActive;
        this.productId = productId;
        this.productOptions = productOptions;
        this.currency = currency;
        this.pricing = new PlanFormulaModel(pricing || {});
        this.recurringInterval = recurringInterval;
        this.invoiceTimeShift = new InvoiceTimeShiftModel(invoiceTimeShift || {});
        if (this.recurringInterval) {
            const {
                unit = null,
                length = null,
                limit = null,
                periodAnchorInstruction = null,
                billingTiming = PlanModel.RecurringIntervalBillingTimings.prepaid,
            } = this.recurringInterval;
            this.recurringInterval = {
                unit, length, limit, billingTiming,
            };
            this.recurringInterval.periodAnchorInstruction = periodAnchorInstruction
                ? new PeriodAnchorInstructionModel(periodAnchorInstruction || {})
                : null;
        }
        this.trial = trial;
        if (this.trial) {
            const {price = null, period = {unit: null, length: null}} = this.trial;
            this.trial = {
                price,
                period,
            };
        }
        this.setup = setup;
        if (this.setup) {
            const {price = null} = this.setup;
            this.setup = {price};
        }
        this.subscriptionCount = Number(subscriptionCount);
        this.createdTime = createdTime;
        this.updatedTime = updatedTime;
        this.customFields = customFields;
    }

    static Recurs = {
        forever: 'forever',
        limited: 'limited',
        never: 'never',
    };

    static RecurringIntervalBillingTimings = {
        prepaid: 'prepaid',
        postpaid: 'postpaid',
    };

    setRecurringType(type) {
        const currentInterval = this.recurringInterval || {};
        const currentInstruction = currentInterval.periodAnchorInstruction;
        const recurringInterval = {
            length: currentInterval.length || null,
            unit: currentInterval.unit || null,
            billingTiming: currentInterval.billingTiming
                || PlanModel.RecurringIntervalBillingTimings.prepaid,
            periodAnchorInstruction: currentInstruction || null,
        };

        if (type === PlanModel.Recurs.forever) {
            this.recurringInterval = {
                ...recurringInterval,
                limit: null,
            };
        } else if (type === PlanModel.Recurs.limited) {
            this.recurringInterval = {
                ...recurringInterval,
                limit: '',
            };
        } else {
            this.recurringInterval = null;
        }
    }

    get isRecurringLimited() {
        return this.recurringInterval !== null && this.recurringInterval.limit !== null;
    }

    get hasSubscriptions() {
        return this.subscriptionCount > 0;
    }

    get hasCustomFields() {
        return Object.keys(this.customFields || {}).length > 0;
    }

    getPayload() {
        const invoiceTimeShift = this.invoiceTimeShift.getPayload() || null;

        const recurringInterval = this.recurringInterval
            ? {...this.recurringInterval}
            : null;

        const periodAnchorInstruction = recurringInterval
            ? recurringInterval.periodAnchorInstruction
            : null;

        if (periodAnchorInstruction) {
            recurringInterval.periodAnchorInstruction = periodAnchorInstruction.getPayload();
        }

        const pricing = this.pricing
            ? this.pricing.getPayload()
            : this.pricing;

        const payload = {
            name: this.name,
            isActive: this.isActive,
            productId: this.productId,
            productOptions: this.productOptions,
            currency: this.currency,
            pricing,
            recurringInterval: recurringInterval || null,
            invoiceTimeShift,
            customFields: this.customFields,
            trial: this.trial,
            setup: this.setup,
        };
        if (this.pricing.isBracket) {
            delete payload.pricing.price;
        } else {
            delete payload.pricing.brackets;
        }
        return payload;
    }
}
